// frontend/src/components/UniversalAuditTool.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import LeftPanel from "./universal-audit/LeftPanel";
import { STORAGE_KEY } from "./universal-audit/constants";
import { buildAuditPayload } from "./universal-audit/buildAuditPayload";
import { supabase } from "../lib/supabaseClient";

const DISCLAIMER_TEXT_VERSION = "universal_not_legal_advice_v1";
const LEAD_TOKEN_KEY = "aifp_universal_audit_lead_token";
const LEAD_EMAIL_KEY = "aifp_universal_audit_lead_email";
const GATE_UNLOCKED_KEY = "aifp_universal_audit_gate_unlocked";
const GATE_CONSENT_KEY = "aifp_universal_audit_gate_consent";
const GATE_SOURCE = "universal_audit";
const EMAIL_FUNCTION = "universal_audit_lead_capture";
// AFPES DEV-only Preview fallback. Public key, never the private verification secret.
// The fallback is deliberately restricted to verified DEV Pages hostnames.
// Production has NO fallback: it must use its separately approved widget/configuration.
const AFP_DEV_HCAPTCHA_SITE_KEY = "da889142-5382-452c-8d74-d38477b41a78";
const AFP_DEV_HCAPTCHA_HOSTS = new Set(["cloudflare-dev.ai-fine-protector-v1.pages.dev"]);
// DEV-only fallback; never carry this configuration into Production.
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_UA_HCAPTCHA_SITE_KEY ||
  (typeof window !== "undefined" && AFP_DEV_HCAPTCHA_HOSTS.has(window.location.hostname)
    ? AFP_DEV_HCAPTCHA_SITE_KEY : "");

function currencyForJurKey(key) {
  switch (key) {
    case "UK":
      return { code: "GBP", symbol: "£" };
    case "EU":
      return { code: "EUR", symbol: "€" };
    case "US_FED":
      return { code: "USD", symbol: "$" };
    default:
      return null;
  }
}

function headlineCurrencyFromSelection(selectedJurKeys) {
  const keys = Array.from(selectedJurKeys || []);
  if (!keys.length) return null;
  if (keys.length === 1) return currencyForJurKey(keys[0]) || null;
  return { code: "MIXED", symbol: "" };
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function getStoredFlag(key) {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function getStoredValue(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function setStoredValue(key, value) {
  try {
    if (value == null || value === "") localStorage.removeItem(key);
    else localStorage.setItem(key, String(value));
  } catch {
    // non-blocking
  }
}

function ensureLeadToken() {
  const existing = getStoredValue(LEAD_TOKEN_KEY);
  if (/^[a-f0-9]{64}$/.test(existing)) return existing;
  // A 256-bit bearer capability, never sent in email, notifications or analytics.
  if (!globalThis.crypto?.getRandomValues) throw new Error("Secure browser randomness is required for lead capture.");
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  const token = Array.from(bytes, v => v.toString(16).padStart(2, "0")).join("");
  setStoredValue(LEAD_TOKEN_KEY, token);
  setStoredValue(GATE_UNLOCKED_KEY, "false");
  return token;
}

function safeStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => typeof item === "string" && item.trim()).map((item) => item.trim());
}

async function writeLead(action, row) {
  const { data, error } = await supabase.functions.invoke(EMAIL_FUNCTION, {
    body: { action, ...row },
  });
  if (error || !data?.success) throw error || new Error(data?.error || "Unable to save audit lead");
  return data;
}

export default function UniversalAuditTool({ importedSignal = null }) {
  const navigate = useNavigate();

  const [industry, setIndustry] = useState("");
  const [aiUseCase, setAiUseCase] = useState("");

  const PEOPLE_MAX = 500000;
  const REV_MAX = 5000000000;

  const [peoplePerMonth, setPeoplePerMonth] = useState(0);
  const [annualRevenue, setAnnualRevenue] = useState(0);
  const [annualRevenueCurrency, setAnnualRevenueCurrency] = useState("");

  const [peopleText, setPeopleText] = useState("0");
  const [annualRevenueText, setAnnualRevenueText] = useState("0");

  const [selectedJurKeys, setSelectedJurKeys] = useState(new Set());

  const usStateAggregationMode = "MAX";

  const severityTier = 3;
  const enforcementInputs = {};
  const eventsSnapshot = null;
  const liveRiskMultiplier = 1.0;

  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const [emailGateUnlocked, setEmailGateUnlocked] = useState(() => getStoredFlag(GATE_UNLOCKED_KEY) && /^[a-f0-9]{64}$/.test(getStoredValue(LEAD_TOKEN_KEY)));
  const [leadEmail, setLeadEmail] = useState(() => getStoredValue(LEAD_EMAIL_KEY));
  const [marketingConsent, setMarketingConsent] = useState(() => getStoredFlag(GATE_CONSENT_KEY));
  const [gateError, setGateError] = useState("");
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [humanToken, setHumanToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);

  const runV3 = useMemo(() => {
    try {
      const mod = require("../lib/universalRiskEngine_v3.js");
      return mod.runUniversalRiskEngineV3 || mod.default;
    } catch {
      return null;
    }
  }, []);

  const headlineCurrency = useMemo(() => {
    return headlineCurrencyFromSelection(selectedJurKeys);
  }, [selectedJurKeys]);

  function normalizeDigitsToNumber(raw, max) {
    const digits = String(raw || "").replace(/[^\d]/g, "");
    const n = digits ? Math.min(Number(digits), max) : 0;
    return Number.isFinite(n) ? n : 0;
  }

  function onPeopleTextChange(e) {
    const n = normalizeDigitsToNumber(e.target.value, PEOPLE_MAX);
    setPeoplePerMonth(n);
    setPeopleText(n.toLocaleString("en-US"));
  }

  function onRevenueTextChange(e) {
    const n = normalizeDigitsToNumber(e.target.value, REV_MAX);
    setAnnualRevenue(n);
    setAnnualRevenueText(n.toLocaleString("en-US"));
  }

  function toggleJur(k) {
    if (!["UK", "EU", "US_FED"].includes(k)) return;
    setSelectedJurKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);

      return next;
    });
  }

  const buildLeadRow = useCallback(
    (overrides = {}) => ({
      lead_token: ensureLeadToken(),
      email: String(leadEmail || "").trim().toLowerCase() || null,
      source: GATE_SOURCE,
      consent: !!marketingConsent,
      captured_at: new Date().toISOString(),
      jurisdictions: safeStringArray(Array.from(selectedJurKeys || [])),
      industry: industry || null,
      niche_description: aiUseCase || null,
      annual_revenue: Number(annualRevenue) || 0,
      monthly_people: Number(peoplePerMonth) || 0,
      ...overrides,
    }),
    [leadEmail, marketingConsent, selectedJurKeys, industry, aiUseCase, annualRevenue, peoplePerMonth]
  );

  const buildEnrichmentRow = useCallback(
    (auditPayload, auditCompleted = false) => {
      const inputs = auditPayload?.inputs || {};
      const v2 = auditPayload?.v2 || {};

      return buildLeadRow({
        jurisdictions: safeStringArray(inputs?.jurisdictions_selected),
        industry: inputs?.industry || null,
        niche_description: inputs?.ai_use_case || null,
        annual_revenue: Number(inputs?.annual_revenue_estimate) || 0,
        monthly_people: Number(inputs?.humans_impacted) || 0,
        exposure_high: auditPayload?.regulatory_preview_version ? null : Number(v2?.structured_worst_case?.exposure_native) || 0,
        exposure_expected: auditPayload?.regulatory_preview_version ? null : Number(v2?.floor?.exposure_native) || 0,
        exposure_low: auditPayload?.regulatory_preview_version ? null : Number(v2?.floor?.exposure_native) || 0,
        audit_completed: !!auditCompleted,
      });
    },
    [buildLeadRow]
  );

  async function handleGateSubmit(e) {
    e.preventDefault();

    const cleanEmail = String(leadEmail || "").trim().toLowerCase();

    if (!isValidEmail(cleanEmail)) {
      setGateError("Please enter a valid email address.");
      return;
    }

    if (gateSubmitting) return;
    if (!HCAPTCHA_SITE_KEY || !humanToken) {
      setGateError("Please complete the human verification check.");
      return;
    }
    if (!marketingConsent) {
      setGateError("Please confirm consent to marketing communication before continuing.");
      return;
    }

    setGateError("");
    setGateSubmitting(true);

    ensureLeadToken();
    setStoredValue(LEAD_EMAIL_KEY, cleanEmail);
    setStoredValue(GATE_CONSENT_KEY, "true");

    const leadRow = buildLeadRow({
      email: cleanEmail,
      consent: true,
      captured_at: new Date().toISOString(),
      hcaptcha_token: humanToken,
    });

    try {
      await writeLead("capture_email", leadRow);

      setStoredValue(GATE_UNLOCKED_KEY, "true");
      setEmailGateUnlocked(true);
    } catch (error) {
      console.error("Universal Audit lead insert failed:", error);
      setGateError("We could not open the audit right now. Please try again.");
      setHumanToken("");
      setCaptchaReset((value) => value + 1);
    } finally {
      setGateSubmitting(false);
    }
  }

  useEffect(() => {
    if (!emailGateUnlocked) return;
    if (!leadEmail || !marketingConsent) return;

    const timer = window.setTimeout(() => {
      writeLead("progress_update", buildLeadRow()).catch((error) => {
        console.error("Universal Audit progress update failed:", error);
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [emailGateUnlocked, leadEmail, marketingConsent, buildLeadRow]);

  const calculateDisabledReason = useMemo(() => {
    if (!emailGateUnlocked) return "Enter your email to unlock the free audit.";
    if (!runV3) return "Risk engine is not available (module load failed).";
    if (!selectedJurKeys || selectedJurKeys.size === 0) return "Select at least one jurisdiction to continue.";
    if (!disclaimerAccepted) return "Please acknowledge the disclaimer to continue.";
    return "";
  }, [emailGateUnlocked, runV3, selectedJurKeys, disclaimerAccepted]);

  async function onCalculate() {
    if (!emailGateUnlocked) {
      window.alert("Please enter your email to unlock the free audit.");
      return;
    }

    if (!runV3) {
      window.alert("Risk engine is not available (module load failed).");
      return;
    }

    if (!selectedJurKeys || selectedJurKeys.size === 0) {
      window.alert("Please select at least one jurisdiction to continue.");
      return;
    }

    if (!disclaimerAccepted) {
      window.alert("Please acknowledge that this is not legal advice before calculating exposure.");
      return;
    }

    let v3;
    try {
      v3 = runV3({
      industry,
      niche: "",
      aiUseCase,
      peoplePerMonth,
      annualRevenue,
      annualRevenueCurrency,
      selectedJurKeys,
      selectedStates: new Set(),
      usStateAggregationMode,
      severityTier,
      enforcementInputs,
      eventsSnapshot,
      });
    } catch (error) {
      window.alert(error?.message || "Unable to prepare regulatory preview.");
      return;
    }

    const payload = buildAuditPayload({
      industry,
      niche: "",
      aiUseCase,
      peoplePerMonth,
      annualRevenue,
      annualRevenueCurrency,
      selectedJurKeys,
      selectedStates: new Set(),
      usStateAggregationMode,
      v3_engine_output: v3,
      severityTier,
      enforcementInputs,
      eventsSnapshot,
      importedBanner: importedSignal,
      liveRiskMultiplier,
      disclaimer_acknowledged: true,
      disclaimer_text_version: DISCLAIMER_TEXT_VERSION,
    });

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      window.alert("Unable to store results (storage may be full). Please clear site data and retry.");
      return;
    }

    const enrichmentRow = buildEnrichmentRow(payload, true);

    try {
      // A single server action persists the completion and claims its notification.
      await writeLead("complete_audit", enrichmentRow);
    } catch (error) {
      console.error("Universal Audit completion save failed:", error);
      window.alert("We could not save your completed audit. Please try again.");
      return;
    }

    navigate("/universal-audit/results");
  }

  return (
    <div className="w-full space-y-6">
      {!emailGateUnlocked ? (
        <section className="rounded-[28px] border border-blue-200 bg-white p-6 shadow-xl shadow-blue-950/5 md:p-8">
          <div className="mx-auto max-w-3xl">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
              Free Executive Preview
            </div>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
              Access Your Free Universal Risk Audit
            </h2>
            <p className="mt-3 text-sm text-slate-700 md:text-base">
              Enter your email to begin your free audit.
            </p>
            <p className="mt-2 text-sm text-slate-600">
              A free executive preview of relevant regulatory penalty frameworks and illustrative financial exposure scenarios, based on the information you provide.
            </p>
            <p className="mt-3 text-sm font-medium text-slate-600">
              Your information is securely handled and never shared.
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleGateSubmit}>
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700" htmlFor="universal-audit-email">
                  Email address
                </label>
                <input
                  id="universal-audit-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={leadEmail}
                  onChange={(e) => {
                    setLeadEmail(e.target.value);
                    if (gateError) setGateError("");
                  }}
                  placeholder="you@company.com"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                />
              </div>

              <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => {
                    setMarketingConsent(e.target.checked);
                    if (gateError) setGateError("");
                  }}
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                />
                <span>
                  I consent to marketing communication from AI Fine Protector. You may unsubscribe at any time.
                </span>
              </label>

              <HCaptchaChallenge siteKey={HCAPTCHA_SITE_KEY} onToken={setHumanToken} resetSignal={captchaReset} />
              {gateError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
                  {gateError}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={gateSubmitting}
                className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {gateSubmitting ? "Opening Audit..." : "Continue to Free Audit"}
              </button>
            </form>
          </div>
        </section>
      ) : null}

      {emailGateUnlocked ? (
        <LeftPanel
          industry={industry}
          setIndustry={setIndustry}
          aiUseCase={aiUseCase}
          setAiUseCase={setAiUseCase}
          niche={""}
          setNiche={() => {}}
          PEOPLE_MAX={PEOPLE_MAX}
          peoplePerMonth={peoplePerMonth}
          peopleText={peopleText}
          onPeopleTextChange={onPeopleTextChange}
          setPeopleFromSlider={(n) => {
            const v = Math.max(0, Math.min(PEOPLE_MAX, Number(n) || 0));
            setPeoplePerMonth(v);
            setPeopleText(v.toLocaleString("en-US"));
          }}
          REV_MAX={REV_MAX}
          annualRevenue={annualRevenue}
          annualRevenueText={annualRevenueText}
          annualRevenueCurrency={annualRevenueCurrency}
          setAnnualRevenueCurrency={setAnnualRevenueCurrency}
          onRevenueTextChange={onRevenueTextChange}
          setRevenueFromSlider={(n) => {
            const v = Math.max(0, Math.min(REV_MAX, Number(n) || 0));
            setAnnualRevenue(v);
            setAnnualRevenueText(v.toLocaleString("en-US"));
          }}
          selectedJurKeys={selectedJurKeys}
          toggleJur={toggleJur}
          headlineCurrency={headlineCurrency}
          disclaimerAccepted={disclaimerAccepted}
          setDisclaimerAccepted={setDisclaimerAccepted}
          calculateDisabledReason={calculateDisabledReason}
          onCalculate={onCalculate}
        />
      ) : null}
    </div>
  );
}

function HCaptchaChallenge({ siteKey, onToken, resetSignal }) {
  const container = React.useRef(null);
  const [challengeError, setChallengeError] = React.useState("");
  React.useEffect(() => {
    if (!siteKey || !container.current) return undefined;
    let stopped = false;
    let widget = null;
    let interval = null;
    let deadline = null;
    const scriptId = "afp-ua-hcaptcha-script";
    const scriptUrl = "https://js.hcaptcha.com/1/api.js?render=explicit";
    setChallengeError("");
    onToken("");
    const fail = () => {
      if (stopped) return;
      if (interval !== null) window.clearInterval(interval);
      interval = null;
      if (deadline !== null) window.clearTimeout(deadline);
      deadline = null;
      onToken("");
      setChallengeError("Human verification is unavailable. Please reload this page and try again.");
    };
    const mount = () => {
      if (stopped || widget !== null || !window.hcaptcha || !container.current) return;
      try {
        widget = window.hcaptcha.render(container.current, {
          sitekey: siteKey,
          callback: (token) => { if (!stopped) onToken(token || ""); },
          "expired-callback": () => { if (!stopped) onToken(""); },
          "error-callback": () => { if (!stopped) onToken(""); },
        });
        if (interval !== null) window.clearInterval(interval);
        interval = null;
        if (deadline !== null) window.clearTimeout(deadline);
        deadline = null;
      } catch { fail(); }
    };
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      script.addEventListener("error", fail);
      document.head.appendChild(script);
    } else {
      script.addEventListener("error", fail);
    }
    interval = window.setInterval(mount, 200);
    deadline = window.setTimeout(fail, 12000);
    mount();
    return () => {
      stopped = true;
      script.removeEventListener("error", fail);
      if (interval !== null) window.clearInterval(interval);
      if (deadline !== null) window.clearTimeout(deadline);
      if (widget !== null && window.hcaptcha) {
        try { window.hcaptcha.remove(widget); } catch { /* cleanup only */ }
      }
    };
  }, [siteKey, onToken, resetSignal]);
  return siteKey
    ? <div><div ref={container} aria-label="Human verification" />{challengeError ? <p role="alert" className="mt-2 text-sm text-red-700">{challengeError}</p> : null}</div>
    : <p className="text-sm text-red-700">Human verification is not configured. Please try again later.</p>;
}
